import {
  BUILDING_KEYS,
  TERRAIN_KEYS,
  TURN_SIDES
} from "../../../game/core/constants.js";
import { getBuildingArmorBonusForType } from "../../../game/content/buildings.js";
import { getPositionArmorBonus } from "../../../game/simulation/combatResolver.js";
import { buildFocusedTile, describeUnit } from "../../../game/simulation/battlePresentation.js";
import {
  getBattleHudArmorIconFileName,
  formatRangeLabel,
  getBattleHudArmorIconUrl,
  getBattleHudWeaponIconUrl,
  renderBattleHudStatBackground,
  renderSelectionIcon
} from "../../shared/unitStatPresentation.js";

function getMeterWidthPercent(current, maximum) {
  if (!Number.isFinite(current) || !Number.isFinite(maximum) || maximum <= 0) {
    return 0;
  }

  return Math.max(0, Math.min(100, (current / maximum) * 100));
}

function getBuildingIconName(buildingType) {
  switch (buildingType) {
    case BUILDING_KEYS.COMMAND:
      return "command";
    case BUILDING_KEYS.BARRACKS:
      return "barracks";
    case BUILDING_KEYS.MOTOR_POOL:
      return "motor-pool";
    case BUILDING_KEYS.AIRFIELD:
      return "airfield";
    case BUILDING_KEYS.SECTOR:
      return "sector";
    case BUILDING_KEYS.HOSPITAL:
      return "hospital";
    case BUILDING_KEYS.REPAIR_STATION:
      return "repair-station";
    default:
      return "building";
  }
}

function getTerrainIconName(terrainId) {
  switch (terrainId) {
    case TERRAIN_KEYS.PLAIN:
      return "plain";
    case TERRAIN_KEYS.ROAD:
      return "road";
    case TERRAIN_KEYS.FOREST:
      return "forest";
    case TERRAIN_KEYS.MOUNTAIN:
      return "mountain";
    case TERRAIN_KEYS.WATER:
      return "water";
    case TERRAIN_KEYS.RIDGE:
      return "ridge";
    default:
      return "terrain";
  }
}

const CORRUPTED_ICON_URL = "./assets/img/icons/battle-hud/conditions/corrupted.png";
const SLOW_ICON_URL = "./assets/img/icons/battle-hud/conditions/slow.png";
const BURN_ICON_URL = "./assets/img/icons/battle-hud/conditions/burn.png";

function sanitizeIdSegment(value) {
  return String(value ?? "stat").replace(/[^a-zA-Z0-9_-]/g, "-");
}

function renderStatTooltip(label, breakdown, tooltipId) {
  const sources = breakdown?.sources ?? [];

  if (!sources.length || !tooltipId) {
    return "";
  }

  return `
    <div class="selection-stat-tooltip" id="${tooltipId}" role="tooltip">
      <div class="selection-stat-tooltip__header">
        <span>${label} modifier</span>
        <strong>${breakdown.modifier === 0 ? "0" : `${breakdown.modifier > 0 ? "+" : ""}${breakdown.modifier}`}</strong>
      </div>
      <div class="selection-stat-tooltip__sources">
        ${sources
          .map(
            (source) => `
              <div class="selection-stat-tooltip__source" data-source-type="${source.type}">
                <span class="selection-stat-tooltip__source-name">${source.name}</span>
                <strong>${source.amount}</strong>
                ${source.detail ? `<small>${source.detail}</small>` : ""}
              </div>
            `
          )
          .join("")}
      </div>
    </div>
  `;
}

function renderStatCell(
  iconName,
  label,
  value,
  { isCorrupted = false, isSlowed = false, breakdown = null, tooltipId = "" } = {}
) {
  const conditionLabels = [];
  const hasTooltip = Boolean(tooltipId && (breakdown?.sources ?? []).length > 0);

  if (isCorrupted) {
    conditionLabels.push("corrupted");
  }

  if (isSlowed) {
    conditionLabels.push("slowed");
  }

  const ariaLabel = `${label} ${value}${conditionLabels.length ? ` ${conditionLabels.join(" ")}` : ""}`;
  const conditionIcon = isCorrupted
    ? {
        src: CORRUPTED_ICON_URL,
        alt: "Corrupted",
        className: "selection-stat__condition-icon selection-stat__condition-icon--corrupted"
      }
    : isSlowed
      ? {
          src: SLOW_ICON_URL,
          alt: "Slowed",
          className: "selection-stat__condition-icon selection-stat__condition-icon--slow"
        }
      : null;

  return `
    <div class="selection-stat-wrap">
      <div
        class="selection-stat${isCorrupted ? " selection-stat--corrupted" : ""}${isSlowed ? " selection-stat--slowed" : ""}${hasTooltip ? " selection-stat--modified" : ""}"
        aria-label="${ariaLabel}"
        ${hasTooltip ? `tabindex="0" aria-describedby="${tooltipId}"` : ""}
      >
        ${renderBattleHudStatBackground(iconName)}
        ${
          conditionIcon
            ? `
              <img
                class="${conditionIcon.className}"
                src="${conditionIcon.src}"
                alt="${conditionIcon.alt}"
                loading="lazy"
                decoding="async"
              />
            `
            : ""
        }
        <div class="selection-stat__content">
          <span class="selection-stat__label">${label}</span>
          <strong>${value}</strong>
        </div>
      </div>
      ${renderStatTooltip(label, breakdown, tooltipId)}
    </div>
  `;
}

function formatProfileName(value, fallback = "Unknown") {
  if (!value) {
    return fallback;
  }

  return value
    .split("_")
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

function getWeaponClassIconFileName(weaponClass) {
  return weaponClass ? `${weaponClass.replaceAll("_", "-")}.png` : null;
}

function getArmorClassIconFileName(armorClass) {
  return armorClass ? getBattleHudArmorIconFileName(armorClass) : null;
}

function renderLoadoutSection(iconUrl, label, value) {
  return `
    <div class="selection-loadout-card">
      ${
        iconUrl
          ? `<img class="selection-loadout-card__icon" src="${iconUrl}" alt="" loading="lazy" decoding="async" />`
          : ""
      }
      <div class="selection-loadout-card__copy">
        <span>${label}</span>
        <strong>${value}</strong>
      </div>
    </div>
  `;
}

function getGearStatusLabel(attachedGear) {
  if (!attachedGear) {
    return "";
  }

  if (Number.isFinite(attachedGear.ammo)) {
    return `${attachedGear.ammo} ammo`;
  }

  if (attachedGear.slot === "gear-field-meds") {
    return "1 use";
  }

  return "";
}

function renderGearLoadoutSection(attachedGear, { showWhenEmpty = true, tooltipIdSuffix = "" } = {}) {
  if (!attachedGear && !showWhenEmpty) {
    return "";
  }

  const gearName = attachedGear?.name ?? "None";
  const gearStatusLabel = getGearStatusLabel(attachedGear);
  const tooltipId = attachedGear?.slot ? `selection-gear-tooltip-${attachedGear.slot}-${tooltipIdSuffix}` : "";

  return `
    <div class="selection-loadout-card selection-loadout-card--gear${attachedGear ? " selection-loadout-card--gear-equipped" : ""}">
      <div class="selection-loadout-card__copy selection-loadout-card__copy--gear">
        <span>Gear</span>
        <div class="selection-loadout-card__title-row">
          <strong>${gearName}</strong>
          ${gearStatusLabel ? `<span class="selection-loadout-card__meta">${gearStatusLabel}</span>` : ""}
        </div>
      </div>
      ${
        attachedGear
          ? `
            <button
              class="selection-loadout-card__info"
              type="button"
              aria-label="${gearName} details"
              aria-describedby="${tooltipId}"
            >
              i
            </button>
            <div class="selection-loadout-tooltip" id="${tooltipId}" role="tooltip">
              <div class="selection-loadout-tooltip__header">
                <strong class="selection-loadout-tooltip__title">${gearName}</strong>
                ${gearStatusLabel ? `<span class="selection-loadout-tooltip__meta">${gearStatusLabel}</span>` : ""}
              </div>
              ${(attachedGear.detailLines ?? []).map((line) => `<p>${line}</p>`).join("")}
            </div>
          `
          : ""
      }
    </div>
  `;
}

function renderTerrainLoadoutSection(terrain) {
  if (!terrain) {
    return "";
  }

  return `
    <div class="selection-loadout-card selection-loadout-card--terrain">
      <span class="selection-icon selection-icon--feature selection-loadout-card__badge" aria-hidden="true">
        ${renderSelectionIcon(getTerrainIconName(terrain.id))}
      </span>
      <div class="selection-loadout-card__copy selection-loadout-card__copy--terrain">
        <span>Terrain</span>
        <strong>${terrain.label ?? "Unknown"}</strong>
        <p class="selection-loadout-card__detail">Armor bonus: +${terrain.armorBonus ?? 0}</p>
      </div>
    </div>
  `;
}

function renderProfileSummary(unit) {
  const armorName = unit?.armorClass ? `${formatProfileName(unit.armorClass)} Armor` : "Unarmored";
  const weaponIconFileName = getWeaponClassIconFileName(unit.weaponClass);
  const armorIconFileName = getArmorClassIconFileName(unit.armorClass);

  return `
    <div class="selection-loadout-grid">
      ${renderLoadoutSection(
        weaponIconFileName ? getBattleHudWeaponIconUrl(weaponIconFileName) : "",
        "Weapon",
        formatProfileName(unit.weaponClass, "Unarmed")
      )}
      ${renderLoadoutSection(
        armorIconFileName ? getBattleHudArmorIconUrl(armorIconFileName) : "",
        "Armor",
        armorName
      )}
    </div>
  `;
}

function renderHealthBar(unit) {
  const healthRatio = getMeterWidthPercent(unit.hp, unit.maxHealth);

  return `
    <div class="selection-health">
      <span class="selection-health__label">HP</span>
      <div class="selection-health__bar" aria-label="Health ${unit.hp} out of ${unit.maxHealth}">
        <div
          class="selection-health__fill"
          data-meter-fill="hp"
          data-meter-value="${healthRatio}"
          style="width:${healthRatio}%"
        ></div>
        <span class="selection-health__value">${unit.hp}/${unit.maxHealth}</span>
      </div>
    </div>
  `;
}

function renderUnitStatGrid(unit) {
  const statBreakdowns = unit.statBreakdowns ?? {};
  const unitId = sanitizeIdSegment(unit.id);

  return `
    <div class="selection-stat-grid">
      ${renderStatCell("attack", "ATK", statBreakdowns.attack?.label ?? unit.attack, {
        isCorrupted: unit.corruptedStat === "attack",
        breakdown: statBreakdowns.attack,
        tooltipId: `selection-stat-tooltip-${unitId}-attack`
      })}
      ${renderStatCell("armor", "ARM", statBreakdowns.armor?.label ?? `${unit.armor}`, {
        isCorrupted: unit.corruptedStat === "armor",
        breakdown: statBreakdowns.armor,
        tooltipId: `selection-stat-tooltip-${unitId}-armor`
      })}
      ${renderStatCell("movement", "MOV", statBreakdowns.movement?.label ?? unit.movement, {
        isSlowed: unit.isSlowed,
        breakdown: statBreakdowns.movement,
        tooltipId: `selection-stat-tooltip-${unitId}-movement`
      })}
      ${renderStatCell("range", "RNG", statBreakdowns.range?.label ?? formatRangeLabel(unit.minRange, unit.maxRange), {
        isCorrupted: unit.corruptedStat === "range",
        breakdown: statBreakdowns.range,
        tooltipId: `selection-stat-tooltip-${unitId}-range`
      })}
      ${renderStatCell("ammo", "AMMO", `${unit.ammo}/${unit.ammoMax}`, {
        isCorrupted: unit.corruptedStat === "ammo"
      })}
      ${renderStatCell("stamina", "STA", `${unit.stamina}/${unit.staminaMax}`, {
        isCorrupted: unit.corruptedStat === "stamina"
      })}
    </div>
  `;
}

function renderExperienceBar(unit, experiencePresentation = null) {
  const displayedExperience = experiencePresentation?.experience ?? unit.experience;
  const displayedThreshold = experiencePresentation?.experienceToNextLevel ?? unit.experienceToNextLevel;
  const displayedRatio = experiencePresentation?.ratio ?? unit.experienceRatio;
  const experienceRatio = Math.max(6, displayedRatio * 100);

  return `
    <div class="selection-section selection-section--xp">
      <div class="selection-header">
        <strong>Experience</strong>
        <span data-experience-value>${Math.round(displayedExperience)}/${displayedThreshold}</span>
      </div>
      <div class="meter meter--exp">
        <div class="meter__bar">
          <div
            data-meter-fill="xp"
            data-meter-value="${experienceRatio}"
            data-experience-threshold="${displayedThreshold}"
            style="width:${experienceRatio}%"
          ></div>
        </div>
      </div>
    </div>
  `;
}

function renderUnitSummary(
  unit,
  {
    showExperience = false,
    showLoadout = true,
    showGear = true,
    terrainMarkup = "",
    experiencePresentation = null
  } = {}
) {
  const attachedGear = unit.gear ?? null;
  const displayedLevel = experiencePresentation?.level ?? unit.level;

  return `
    <div class="selection-section selection-section--unit" data-selection-unit-card="${unit.id ?? ""}">
      <div class="selection-unit-heading">
      <div class="selection-unit-heading__title">
        <strong>${unit.name}</strong>
        <span class="selection-level-badge" data-experience-level aria-label="Level ${displayedLevel}">${displayedLevel}</span>
        ${
          unit.isBurned
            ? `
                <img
                  class="selection-unit-heading__condition-icon"
                  src="${BURN_ICON_URL}"
                  alt="Burning"
                  loading="lazy"
                  decoding="async"
                />
              `
              : ""
          }
        </div>
      </div>
      ${showExperience ? renderExperienceBar(unit, experiencePresentation) : ""}
      ${renderHealthBar(unit)}
      ${renderUnitStatGrid(unit)}
      ${
        unit.isHostageCarrier
          ? renderFeatureSection(
              "command",
              "Hostage Carrier",
              ["Movement -1. Cannot attack. Deliver the hostage to your HQ."]
            )
          : ""
      }
      ${
        showLoadout || showGear || terrainMarkup
          ? `
              <div class="selection-loadout-stack">
                ${showLoadout ? renderProfileSummary(unit) : ""}
                ${
                  showGear
                    ? renderGearLoadoutSection(attachedGear, {
                        showWhenEmpty: showLoadout,
                        tooltipIdSuffix: unit.id ?? "unit"
                      })
                    : ""
                }
                ${terrainMarkup}
              </div>
            `
          : ""
      }
    </div>
  `;
}

function renderFeatureSection(iconName, title, lines, modifierClass = "") {
  return `
    <div class="selection-section ${modifierClass}">
      <div class="selection-feature">
        <span class="selection-icon selection-icon--feature" aria-hidden="true">${renderSelectionIcon(iconName)}</span>
        <div class="selection-feature__body">
          <strong>${title}</strong>
          ${lines.map((line) => `<p>${line}</p>`).join("")}
        </div>
      </div>
    </div>
  `;
}

function formatGridPosition(tile) {
  if (!tile || !Number.isInteger(tile.x) || !Number.isInteger(tile.y)) {
    return "Unknown";
  }

  return `Sector ${String.fromCharCode(65 + tile.y)}${tile.x + 1}`;
}

function getUnitEffectSummary(unit) {
  const effects = [];

  if (unit?.isBurned) {
    effects.push("Burn");
  }

  if (unit?.corruptedStat) {
    effects.push("Corrupted");
  }

  if (unit?.isSlowed) {
    effects.push("Slowed");
  }

  return effects.length > 0 ? effects.join(", ") : "None";
}

function renderTargetIntelCard(tile, { forecast = null } = {}) {
  if (!tile?.unit) {
    return "";
  }

  const unit = tile.unit;
  const armorName = unit?.armorClass ? formatProfileName(unit.armorClass) : "Unknown";
  const rows = [
    ["Armor", armorName],
    ["Position", formatGridPosition(tile)],
    ["Terrain", tile.terrain?.label ?? "Unknown"],
    ["Effect", getUnitEffectSummary(unit)]
  ];

  if (forecast) {
    const counterLabel = forecast.received ? `${forecast.received.min}-${forecast.received.max}` : "0";
    rows.push(["Forecast", `${forecast.dealt.min}-${forecast.dealt.max} | Counter ${counterLabel}`]);
  }

  return `
    <div class="card-block card-block--target-intel">
      <h3>Target Intel</h3>
      <div class="target-intel-card">
        ${renderUnitSummary(unit, { showExperience: false, showLoadout: false })}
        <div class="target-intel-card__rows">
          ${rows
            .map(
              ([label, value]) => `
                <div class="target-intel-card__row">
                  <span>${label}</span>
                  <strong>${value}</strong>
                </div>
              `
            )
            .join("")}
        </div>
      </div>
    </div>
  `;
}

export function renderSelectionDetails(
  selectedTile,
  { title, emptyTitle, emptyBody, experiencePresentation = null } = {}
) {
  if (!selectedTile) {
    return `
      <div class="card-block">
        <h3>${emptyTitle ?? title ?? "Selection"}</h3>
        <p>${emptyBody ?? "Select a unit, building, or tile on the battlefield to inspect it here."}</p>
      </div>
    `;
  }

  const unit = selectedTile.unit;
  const building = selectedTile.building;

  return `
      <div class="card-block">
        ${title ? `<h3>${title}</h3>` : ""}
        ${
        unit
          ? renderUnitSummary(unit, {
              showExperience: true,
              experiencePresentation: experiencePresentation?.[unit.id] ?? null,
              terrainMarkup: building ? "" : renderTerrainLoadoutSection(selectedTile.terrain)
            })
          : ""
      }
      ${
        building
          ? renderFeatureSection(
              getBuildingIconName(building.type),
              building.name,
              [
                `Owner: ${building.ownerLabel}`,
                `Armor bonus: +${building.armorBonus ?? getBuildingArmorBonusForType(building.type)}`
              ].filter(Boolean)
            )
          : ""
      }
      ${
        building
          ? ""
          : unit
            ? ""
            : renderFeatureSection(
                getTerrainIconName(selectedTile.terrain.id),
                selectedTile.terrain.label,
                [`Armor bonus: +${selectedTile.terrain.armorBonus ?? 0}`],
                "selection-section--terrain"
              )
      }
    </div>
  `;
}

function getSelectedTileFocusSide(battleSnapshot, selectedTile) {
  if (!battleSnapshot || !selectedTile) {
    return null;
  }

  if (selectedTile.unit?.owner) {
    return selectedTile.unit.owner;
  }

  if (selectedTile.building) {
    return TURN_SIDES.PLAYER;
  }

  return TURN_SIDES.PLAYER;
}

export function getFocusTileForSide(battleSnapshot, battleUi, side) {
  const selectedTile = battleSnapshot.presentation?.selectedTile;
  const selectedTileSide = getSelectedTileFocusSide(battleSnapshot, selectedTile);

  if (selectedTile && selectedTileSide === side) {
    return selectedTile;
  }

  const focus = side === TURN_SIDES.PLAYER ? battleUi?.playerFocus : battleUi?.enemyFocus;
  return buildFocusedTile(battleSnapshot, focus);
}

function getHoveredTargetReference(battleSnapshot, hoveredTile) {
  const pendingAction = battleSnapshot.presentation?.pendingAction;

  if (!pendingAction?.isTargeting || !hoveredTile) {
    return null;
  }

  const target = battleSnapshot.enemy.units.find(
    (unit) => unit.current.hp > 0 && unit.x === hoveredTile.x && unit.y === hoveredTile.y
  );

  if (!target) {
    return null;
  }

  const forecast = battleSnapshot.presentation?.attackForecasts?.[target.id] ?? null;

  if (!forecast) {
    return null;
  }

  return {
    target,
    forecast
  };
}

function renderTargetReference(battleSnapshot, hoveredTile) {
  const targetReference = getHoveredTargetReference(battleSnapshot, hoveredTile);

  if (!targetReference) {
    return "";
  }

  const { target, forecast } = targetReference;
  const targetTile = buildFocusedTile(battleSnapshot, {
    type: "unit",
    id: target.id
  });

  return renderTargetIntelCard(targetTile, { forecast });
}

export function renderTargetIntelPanel(battleSnapshot, hoveredTile, selectedTile) {
  const hoveredTargetReference = getHoveredTargetReference(battleSnapshot, hoveredTile);

  if (hoveredTargetReference) {
    return renderTargetReference(battleSnapshot, hoveredTile);
  }

  if (selectedTile?.unit?.owner === TURN_SIDES.ENEMY) {
    return renderTargetIntelCard(selectedTile);
  }

  return `
    <div class="card-block">
      <h3>Target Intel</h3>
      <p>Select or target a hostile unit to inspect it here.</p>
    </div>
  `;
}
